import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Save } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';
import EmailProviderSelector from './keys/EmailProviderSelector';
import EmailSenderConfig from './keys/EmailSenderConfig';
/* 2026-02 · CloudflareEmailConfig retired — Cloudflare Email Routing
 * is inbound-only. Outbound is Resend-only now. */
import StripeWebhookConfig from './keys/StripeWebhookConfig';
import StripeConnectWebhookConfig from './keys/StripeConnectWebhookConfig';
import AdminRecoveryEmailConfig from './keys/AdminRecoveryEmailConfig';
import SignupNotificationEmailConfig from './keys/SignupNotificationEmailConfig';
import PlagiarismProviderConfig from './keys/PlagiarismProviderConfig';
import CanvaCredsConfig from './keys/CanvaCredsConfig';
import CompanionIntegrationCard from './keys/CompanionIntegrationCard';
import EmailOpsCard from './keys/EmailOpsCard';
import IntegrationsTogglePanel from './IntegrationsTogglePanel';
import ProviderProbeControls from '@/components/admin/ProviderProbeControls';
import SiteModeCard from './keys/SiteModeCard';
import RouteValveCard from './keys/RouteValveCard';       // Feb-2026 · native-Anthropic safety valve
import WifStatusCard from './keys/WifStatusCard';          // Oct-2026 · WIF / OIDC federation status
import SitemapPingCard from './keys/SitemapPingCard';    // Feb-2026 · Google/Bing sitemap ping
import RetiredBooksPanel from './keys/RetiredBooksPanel'; // Feb-2026 · orphan republish
import IndexNowKeyCard from './keys/IndexNowKeyCard';     // Feb-2026 · IndexNow key management

/** Reusable card for platform-level LLM API keys (OpenAI, Gemini, DeepSeek). */
function LlmKeyCard({ title, description, cfgKey, configuredFlag, rotatedAt, placeholder, testId, onSaved }) {
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ [cfgKey]: value });
      toast.success(value.trim() ? `${title} saved.` : `${title} cleared.`);
      setValue('');
      onSaved?.();
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ [cfgKey]: '' });
      toast.success(`${title} cleared.`);
      setValue('');
      onSaved?.();
    } catch { toast.error('Clear failed'); }
    finally { setBusy(false); }
  };

  // Format rotation timestamp
  const rotatedLabel = (() => {
    if (!rotatedAt) return null;
    try {
      const d = new Date(rotatedAt);
      const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
      const fmt = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      const age = diffDays === 0 ? 'today'
        : diffDays === 1 ? '1 day ago'
        : diffDays < 30 ? `${diffDays} days ago`
        : diffDays < 365 ? `${Math.floor(diffDays / 30)} months ago`
        : `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
      const overdue = diffDays >= 90;
      return { fmt, age, overdue };
    } catch { return null; }
  })();

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[12.5px] font-semibold">{title}</div>
          <div className="text-[11px] text-muted-foreground">
            {description}{' '}
            {configuredFlag
              ? <span className="text-emerald-700 font-medium">Currently configured.</span>
              : <span className="text-amber-700">Not set — using Emergent Universal Key.</span>}
          </div>
        </div>
        {/* Last rotated timestamp badge */}
        {rotatedLabel ? (
          <div
            data-testid={`${testId}-rotated-at`}
            className={`shrink-0 rounded border px-2 py-0.5 text-[10px] leading-tight ${
              rotatedLabel.overdue
                ? 'border-amber-400/60 bg-amber-50/80 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                : 'border-emerald-400/50 bg-emerald-50/60 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
            }`}
            title={`Rotated on ${rotatedLabel.fmt}`}
          >
            <div className="font-medium">{rotatedLabel.overdue ? 'Overdue rotation' : 'Last rotated'}</div>
            <div className="opacity-80">{rotatedLabel.age}</div>
          </div>
        ) : configuredFlag ? (
          <div
            data-testid={`${testId}-rotated-at`}
            className="shrink-0 rounded border border-amber-400/50 bg-amber-50/60 px-2 py-0.5 text-[10px] leading-tight text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            title="No rotation record — key was set before rotation tracking was introduced"
          >
            <div className="font-medium">No rotation record</div>
            <div className="opacity-80">rotate to start tracking</div>
          </div>
        ) : null}
      </div>
      <div className="flex gap-2">
        <div className="relative max-w-md flex-1">
          <Input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={configuredFlag ? `${placeholder} (replace current)` : placeholder}
            className="h-8 font-mono-stat text-[11.5px] pr-8"
            data-testid={`${testId}-input`}
          />
          <button
            type="button"
            onClick={() => setShow(v => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
            data-testid={`${testId}-toggle-show`}
          >
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <Button size="sm" onClick={save} disabled={busy || !value.trim()} className="h-8 gap-1.5" data-testid={`${testId}-save`}>
          <Save className="h-3.5 w-3.5" /> Save
        </Button>
        {configuredFlag && (
          <Button size="sm" variant="ghost" onClick={clear} disabled={busy} className="h-8 gap-1.5 text-muted-foreground" data-testid={`${testId}-clear`}>
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

export default function KeysPanel() {
  const [cfg, setCfg] = useState(null);
  const [stripeKey, setStripeKey] = useState('');
  const [stripeAlertEmail, setStripeAlertEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await adminApi.config();
      setCfg(r);
      setStripeAlertEmail(r?.stripe_alert_email || '');
    } catch { toast.error('Could not load config'); }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ stripe_admin_key: stripeKey });
      toast.success(stripeKey ? 'Admin Stripe key saved.' : 'Admin Stripe key cleared.');
      setStripeKey('');
      reload();
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const clearKey = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ stripe_admin_key: '' });
      toast.success('Admin Stripe key cleared.');
      setStripeKey('');
      reload();
    } catch { toast.error('Clear failed'); }
    finally { setBusy(false); }
  };

  return (
    <section>
      <PanelHeader title="API Keys" subtitle="Application-wide secrets. Per-user keys take precedence; these admin keys are platform-level defaults." onRefresh={reload} />

      {/* Feb-2026 — Launch-day toggle. Pinned above the API-keys block
       * so it's the first thing an admin sees when the store opens. */}
      <SiteModeCard />
      <div className="mt-4 rounded-md border border-border bg-card p-4 space-y-3">
        <div>
          <div className="text-[12.5px] font-semibold">Admin Stripe Secret Key</div>
          <div className="text-[11px] text-muted-foreground">Used by checkout sessions when the customer has not configured their own key. {cfg?.stripe_admin_key_configured ? 'Currently configured.' : 'Not configured — Stripe defaults to the Emergent test key.'}</div>
        </div>
        <div className="flex gap-2">
          <Input type="password" value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} placeholder={cfg?.stripe_admin_key_configured ? 'sk_live_… (replace current)' : 'sk_live_xxxxxxx'} className="h-8 max-w-md font-mono-stat text-[11.5px]" data-testid="admin-stripe-key-input" />
          <Button size="sm" onClick={save} disabled={busy} className="h-8 gap-1.5" data-testid="admin-stripe-key-save"><Save className="h-3.5 w-3.5" /> Save</Button>
          {cfg?.stripe_admin_key_configured && (
            <Button size="sm" variant="ghost" onClick={clearKey} disabled={busy} className="h-8 gap-1.5 text-muted-foreground" data-testid="admin-stripe-key-clear">Clear</Button>
          )}
        </div>
        <ProviderProbeControls
          testEndpoint="/admin/stripe/test-connection"
          alertEmail={stripeAlertEmail}
          onAlertEmailChange={setStripeAlertEmail}
          onAlertEmailSave={async () => {
            await adminApi.saveConfig({ stripe_alert_email: stripeAlertEmail.trim() });
            await reload();
          }}
          lastTestAt={cfg?.stripe_last_test_at || ''}
          lastTestOk={cfg?.stripe_last_test_ok}
          lastTestMessage={cfg?.stripe_last_test_message || ''}
          // Always considered "configured" because Stripe falls back to the
          // Emergent sentinel test key — there's always *something* to probe.
          configured={true}
          testIdPrefix="admin-stripe"
          onAfterTest={reload}
        />
      </div>

      {/* Platform-level LLM keys — allow the admin to enter OpenAI / Gemini
       * keys that act as the platform default when users haven't set their
       * own BYOK key. Resolution order:
       *   1. User's own BYOK key  2. These admin keys  3. Emergent Universal Key */}
      <LlmKeyCard
        title="OpenAI API Key (Platform Default)"
        description="Used for OpenAI GPT calls when no user-level key is set."
        cfgKey="openai_admin_key"
        configuredFlag={cfg?.openai_admin_key_configured}
        rotatedAt={cfg?.openai_admin_key_rotated_at}
        placeholder="sk-proj-…"
        testId="admin-openai-key"
        onSaved={reload}
      />
      <LlmKeyCard
        title="Gemini API Key (Platform Default)"
        description="Used for Gemini calls when no user-level key is set."
        cfgKey="gemini_admin_key"
        configuredFlag={cfg?.gemini_admin_key_configured}
        rotatedAt={cfg?.gemini_admin_key_rotated_at}
        placeholder="AIza…"
        testId="admin-gemini-key"
        onSaved={reload}
      />
      <LlmKeyCard
        title="DeepSeek API Key (Platform Default)"
        description="Used for DeepSeek V4.1 Flash calls when no user-level BYOK key is set. Resolution order: user BYOK → this admin key → env DEEPSEEK_API_KEY."
        cfgKey="deepseek_admin_key"
        configuredFlag={cfg?.deepseek_admin_key_configured}
        rotatedAt={cfg?.deepseek_admin_key_rotated_at}
        placeholder="sk-…"
        testId="admin-deepseek-key"
        onSaved={reload}
      />

      <StripeWebhookConfig cfg={cfg} onSaved={reload} />
      <StripeConnectWebhookConfig cfg={cfg} onSaved={reload} />
      <AdminRecoveryEmailConfig cfg={cfg} onSaved={reload} />
      <SignupNotificationEmailConfig cfg={cfg} onSaved={reload} />
      <EmailProviderSelector cfg={cfg} onSaved={reload} />
      <IntegrationsTogglePanel cfg={cfg} onSaved={reload} />
      <EmailSenderConfig cfg={cfg} onSaved={reload} />
      <EmailOpsCard />
      {/* 2026-02 · CloudflareEmailConfig retired — outbound is Resend-only. */}
      <PlagiarismProviderConfig cfg={cfg} onSaved={reload} />
      <CanvaCredsConfig />
      <CompanionIntegrationCard />
      <RouteValveCard />
      <WifStatusCard />
      <SitemapPingCard />
      <IndexNowKeyCard cfg={cfg} onSaved={reload} />
      <RetiredBooksPanel />
    </section>
  );
}
